import {
  Box,
  Button,
  Card,
  Typography,
  IconButton,
  CircularProgress,
  TextField,
} from "@mui/material";
import { Add, Remove, Check } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useStore } from "../../../app/stores/store";

interface Variant {
  id: number;
  quantity: number;
  base_price: number;
  mrp: number;
  offer: number;
  offer_type: number;
  unit: string;
  is_base_varient: boolean;
  varient_denomination_counter: number;
  varient_denomination_of_uom: string;
  product_inventory_stock?: any;
  quantity_in_cart: number;
}

interface Product {
  id: number;
  title: string;
  unit: string;
  price: number;
  image: string;
  product_varients: Variant[];
  max_quantity_standard: number;
  max_quantity_quick: number;
  total_stock: number;
  base_denomination: number;
  base_denomination_uom: string;
}

interface Props {
  product: Product;
  deliveryType: number; // 1 = Standard, 2 = Quick
  onAddToCart: ({
    productId,
    quantity,
    variantId,
  }: {
    productId: number;
    quantity: number;
    variantId: number;
  }) => Promise<void>;
}

const ProductCard: React.FC<Props> = ({
  product,
  deliveryType,
  onAddToCart,
}) => {
  const { toasterStore } = useStore();
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(0);
  const [quantityInCart, setQuantityInCart] = useState(0);
  const [activeVariantId, setActiveVariantId] = useState<number | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [showInput, setShowInput] = useState(false);

  // Check if product is out of stock
  const isOutOfStock = product?.total_stock < 1;

  // Filter variants based on available stock
  const availableVariants =
    product?.product_varients?.filter(
      (v) => v.varient_denomination_counter <= product?.total_stock
    ) || [];

  useEffect(() => {
    const variantWithCart = product?.product_varients.find(
      (v) => v?.quantity_in_cart > 0
    );
    if (variantWithCart) {
      const cartQuantity = variantWithCart?.quantity_in_cart;
      setQuantityInCart(cartQuantity);
      setQuantity(cartQuantity);
      setInputValue(String(cartQuantity));
      setShowInput(true);

      const sortedVariants = [...product.product_varients].sort(
        (a, b) =>
          b.varient_denomination_counter - a.varient_denomination_counter
      );

      const bestMatchVariant =
        sortedVariants.find(
          (v) => v.varient_denomination_counter <= cartQuantity
        ) || sortedVariants[sortedVariants.length - 1];

      setActiveVariantId(bestMatchVariant?.id);
    } else {
      setQuantityInCart(0);
      setQuantity(0);
      setInputValue("");
      setShowInput(false);
      setActiveVariantId(null);
    }
  }, [product?.product_varients]);

  const getActiveVariant = () => {
    if (activeVariantId) {
      return product.product_varients.find((v) => v.id === activeVariantId)!;
    }
    return [...product.product_varients].sort(
      (a, b) => a.varient_denomination_counter - b.varient_denomination_counter
    )[0];
  };

  const activeVariant = getActiveVariant();
  const totalPrice =
    quantity > 0
      ? quantity * activeVariant?.base_price
      : activeVariant?.base_price;

  // Get max quantity based on delivery type (1 = Standard, 2 = Quick)
  const getMaxQuantity = () => {
    return deliveryType === 2
      ? product.max_quantity_quick
      : product.max_quantity_standard;
  };

  const getDeliveryTypeName = () => {
    return deliveryType === 2 ? "quick" : "standard";
  };

  const handleQuantityChange = async (delta: number) => {
    if (!activeVariant || isOutOfStock) return;

    let newQty = quantity + delta;
    if (quantity === 0 && delta > 0) {
      newQty = activeVariant?.varient_denomination_counter;
    }
    if (newQty < 0) return;

    const sortedVariants = [...product?.product_varients].sort(
      (a, b) => b.varient_denomination_counter - a.varient_denomination_counter
    );

    const bestMatchVariant =
      sortedVariants.find((v) => v.varient_denomination_counter <= newQty) ||
      sortedVariants[sortedVariants.length - 1];

    const variantToUse = bestMatchVariant;

    const availableStock = product?.total_stock;
    if (
      availableStock !== undefined &&
      availableStock !== null &&
      newQty > Number(availableStock)
    ) {
      toasterStore.error("Sorry! We have only limited quantity in stock.");
      return;
    }

    const maxQuantityAllowed = getMaxQuantity();
    if (
      maxQuantityAllowed !== undefined &&
      maxQuantityAllowed !== null &&
      newQty > Number(maxQuantityAllowed)
    ) {
      toasterStore.error(
        `Maximum ${maxQuantityAllowed} units available for ${getDeliveryTypeName()} delivery.`
      );
      return;
    }

    setLoading(true);
    try {
      await onAddToCart({
        productId: product?.id,
        quantity: newQty,
        variantId: variantToUse?.id,
      });
      setQuantity(newQty);
      setQuantityInCart(newQty);
      setInputValue(String(newQty));
      setActiveVariantId(variantToUse?.id);
      if (newQty === 0) {
        setActiveVariantId(null);
        setShowInput(false);
        setInputValue("");
      }
    } catch {
      toasterStore.error("Failed to update cart");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === "") {
      setInputValue("");
      return;
    }

    if (!/^\d+$/.test(value)) {
      return;
    }

    const newQty = Number(value);

    const availableStock = product?.total_stock;
    if (
      availableStock !== undefined &&
      availableStock !== null &&
      newQty > Number(availableStock)
    ) {
      toasterStore.error("Sorry! We have only limited quantity in stock.");
      setInputValue(String(quantity));
      return;
    }

    const maxAllowed = getMaxQuantity();
    if (
      maxAllowed !== undefined &&
      maxAllowed !== null &&
      newQty > Number(maxAllowed)
    ) {
      toasterStore.error(
        `Maximum ${maxAllowed} units available for ${getDeliveryTypeName()} delivery.`
      );
      setInputValue(String(quantity));
      return;
    }

    setInputValue(value);
  };

  const handleInputBlur = async () => {
    const newQty = parseInt(inputValue) || 0;

    if (newQty === quantity) return;

    const sortedVariants = [...product?.product_varients].sort(
      (a, b) => b.varient_denomination_counter - a.varient_denomination_counter
    );

    const bestMatchVariant =
      sortedVariants.find((v) => v.varient_denomination_counter <= newQty) ||
      sortedVariants[sortedVariants.length - 1];

    const availableStock = product?.total_stock;
    if (
      availableStock !== undefined &&
      availableStock !== null &&
      newQty > Number(availableStock)
    ) {
      toasterStore.error("Sorry! We have only limited quantity in stock.");
      setInputValue(String(quantity));
      return;
    }

    const maxQuantityAllowed = getMaxQuantity();
    if (
      maxQuantityAllowed !== undefined &&
      maxQuantityAllowed !== null &&
      newQty > Number(maxQuantityAllowed)
    ) {
      toasterStore.error(
        `Maximum ${maxQuantityAllowed} units available for ${getDeliveryTypeName()} delivery.`
      );
      setInputValue(String(quantity));
      return;
    }

    if (newQty === 0) {
      setInputValue(String(quantity));
      return;
    }

    setLoading(true);
    try {
      await onAddToCart({
        productId: product?.id,
        quantity: newQty,
        variantId: bestMatchVariant?.id,
      });
      setQuantity(newQty);
      setQuantityInCart(newQty);
      setActiveVariantId(bestMatchVariant?.id);
    } catch {
      toasterStore.error("Failed to update cart");
      setInputValue(String(quantity));
    } finally {
      setLoading(false);
    }
  };

  const handleSlabClick = (variantId: number) => {
    if (isOutOfStock) return;
    setActiveVariantId(variantId);
  };

  const handleAddVariant = async (e: React.MouseEvent, variantId: number) => {
    e.stopPropagation();
    if (isOutOfStock) return;

    const selectedVariant = product?.product_varients?.find(
      (v) => v?.id === variantId
    );
    if (!selectedVariant) return;

    const newQty = selectedVariant?.varient_denomination_counter;

    // Check stock availability
    const availableStock = product?.total_stock;
    if (
      availableStock !== undefined &&
      availableStock !== null &&
      newQty > Number(availableStock)
    ) {
      toasterStore.error("Sorry! We have only limited quantity in stock.");
      return;
    }

    // Check max quantity limit
    const maxQuantityAllowed = getMaxQuantity();
    if (
      maxQuantityAllowed !== undefined &&
      maxQuantityAllowed !== null &&
      newQty > Number(maxQuantityAllowed)
    ) {
      toasterStore.error(
        `Maximum ${maxQuantityAllowed} units available for ${getDeliveryTypeName()} delivery.`
      );
      return;
    }

    setLoading(true);
    try {
      await onAddToCart({
        productId: product?.id,
        quantity: newQty,
        variantId: variantId,
      });
      setQuantity(newQty);
      setQuantityInCart(newQty);
      setInputValue(String(newQty));
      setActiveVariantId(variantId);
      setShowInput(true);
    } catch {
      toasterStore.error("Failed to update cart");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!activeVariant || isOutOfStock) return;

    const initialQty = activeVariant?.varient_denomination_counter;

    // Check stock availability
    const availableStock = product?.total_stock;
    if (
      availableStock !== undefined &&
      availableStock !== null &&
      initialQty > Number(availableStock)
    ) {
      toasterStore.error("Sorry! We have only limited quantity in stock.");
      return;
    }

    // Check max quantity limit
    const maxQuantityAllowed = getMaxQuantity();
    if (
      maxQuantityAllowed !== undefined &&
      maxQuantityAllowed !== null &&
      initialQty > Number(maxQuantityAllowed)
    ) {
      toasterStore.error(
        `Maximum ${maxQuantityAllowed} units available for ${getDeliveryTypeName()} delivery.`
      );
      return;
    }

    setLoading(true);
    try {
      await onAddToCart({
        productId: product?.id,
        quantity: initialQty,
        variantId: activeVariant?.id,
      });
      setQuantity(initialQty);
      setQuantityInCart(initialQty);
      setInputValue(String(initialQty));
      setShowInput(true);
    } catch {
      toasterStore.error("Failed to add to cart");
    } finally {
      setLoading(false);
    }
  };

  const sortedVariantsDesc = [...(product?.product_varients || [])].sort(
    (a, b) => b.varient_denomination_counter - a.varient_denomination_counter
  );

  const currentQtyVariantId =
    quantity > 0
      ? (sortedVariantsDesc.find(
          (v) => v.varient_denomination_counter <= quantity
        ) || sortedVariantsDesc[sortedVariantsDesc.length - 1])?.id
      : null;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: "#eee",
        p: 2,
        mb: 2,
        opacity: isOutOfStock ? 0.5 : 1,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* Left: Image + Info */}
        <Box
          display="flex"
          flex={1}
          sx={{
            width: { xs: "100%", md: "auto" },
            flexDirection: { xs: "row", md: "row" },
            mb: { xs: 1, md: 0 },
          }}
        >
          <Box
            sx={{
              width: { xs: 80, md: 100 },
              height: { xs: 80, md: 100 },
              mr: 2,
              flexShrink: 0,
              position: "relative",
            }}
          >
            <img
              src={product?.image}
              alt={product?.title}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
            {isOutOfStock && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="error"
                  sx={{
                    backgroundColor: "white",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                  }}
                >
                  OUT OF STOCK
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            flex={1}
            sx={{
              display: { xs: "flex", md: "block" },
              flexDirection: "column",
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}
            >
              {product?.title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: { xs: "none", md: "block" } }}
            >
              {product?.base_denomination} {product?.base_denomination_uom}
            </Typography>

            {/* Mobile: Quantity and Price below product name */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                justifyContent: "space-between",
                alignItems: "center",
                mt: 0.5,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {product?.base_denomination} {product?.base_denomination_uom}
              </Typography>
              {!isOutOfStock && (
                <Box textAlign="right">
                  <Typography variant="subtitle1" fontWeight={600}>
                    ₹{totalPrice.toFixed(2)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textDecoration: "line-through" }}
                  >
                    ₹
                    {quantity > 1
                      ? (quantity * activeVariant.mrp).toFixed(2)
                      : (activeVariant?.mrp).toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Variant price info - Desktop only */}
            {!isOutOfStock && (
              <Box mt={1} sx={{ display: { xs: "none", md: "block" } }}>
                {availableVariants.map((v) => {
                  return (
                    <Box
                      key={v.id}
                      onClick={() => handleSlabClick(v.id)}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1,
                        border: "1px solid #f0f0f0",
                        borderRadius: 1,
                        mb: 0.5,
                        cursor: "pointer",
                        backgroundColor:
                          activeVariantId === v.id ? "#fff8e1" : "transparent",
                        borderColor:
                          activeVariantId === v.id ? "#ffca28" : "#f0f0f0",
                        "&:hover": {
                          backgroundColor:
                            activeVariantId === v.id ? "#fff3cd" : "#f9f9f9",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={activeVariantId === v.id ? 600 : 400}
                      >
                        ₹{v.varient_denomination_of_uom} for{" "}
                        {v.varient_denomination_counter}+
                      </Typography>
                      {quantity > 0 && currentQtyVariantId === v.id ? (
                        <Check sx={{ color: "#1677ffe6", fontWeight: "bold" }} />
                      ) : (
                        <Typography
                          onClick={(e) => handleAddVariant(e, v.id)}
                          sx={{ color: "#1677ffe6", fontWeight: "bold" }}
                        >
                          ADD
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>

        {/* Right: Price - Desktop only */}
        {!isOutOfStock && (
          <Box textAlign="right" sx={{ display: { xs: "none", md: "block" } }}>
            <Typography variant="h6" fontWeight={600}>
              ₹{totalPrice.toFixed(2)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textDecoration: "line-through" }}
            >
              ₹
              {quantity > 1
                ? (quantity * activeVariant.mrp).toFixed(2)
                : (activeVariant?.mrp).toFixed(2)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Mobile: Variants - Full width */}
      {!isOutOfStock && (
        <Box sx={{ display: { xs: "block", md: "none" }, mb: 2 }}>
          {availableVariants.map((v) => {
            return (
              <Box
                key={v.id}
                onClick={() => handleSlabClick(v.id)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  p: 1,
                  border: "1px solid #f0f0f0",
                  borderRadius: 1,
                  mb: 0.5,
                  cursor: "pointer",
                  backgroundColor:
                    activeVariantId === v.id ? "#fff8e1" : "transparent",
                  borderColor: activeVariantId === v.id ? "#ffca28" : "#f0f0f0",
                  "&:hover": {
                    backgroundColor:
                      activeVariantId === v.id ? "#fff3cd" : "#f9f9f9",
                  },
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={activeVariantId === v.id ? 600 : 400}
                  sx={{ fontSize: "0.85rem" }}
                >
                  ₹{v.varient_denomination_of_uom} for{" "}
                  {v.varient_denomination_counter}+
                </Typography>
                {quantity > 0 && currentQtyVariantId === v.id ? (
                  <Check sx={{ color: "#1677ffe6", fontWeight: "bold" }} />
                ) : (
                  <Typography
                    onClick={(e) => handleAddVariant(e, v.id)}
                    sx={{ color: "#1677ffe6", fontWeight: "bold" }}
                  >
                    ADD
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Quantity Controls */}
      <Box
        mt={2}
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
        sx={{
          mt: { xs: 0, md: 2 },
        }}
      >
        {isOutOfStock ? (
          <Button
            variant="outlined"
            size="small"
            disabled
            sx={{
              color: "error.main",
              borderColor: "error.main",
              "&.Mui-disabled": {
                color: "error.main",
                borderColor: "error.main",
                opacity: 0.7,
              },
            }}
          >
            Out of Stock
          </Button>
        ) : showInput && quantityInCart > 0 ? (
          <>
            <IconButton
              size="small"
              onClick={() => handleQuantityChange(-1)}
              disabled={loading}
            >
              <Remove fontSize="small" />
            </IconButton>
            <TextField
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              disabled={loading}
              size="small"
              sx={{
                width: "50px",
                "& .MuiOutlinedInput-root": {
                  height: "32px",
                  "& input": {
                    textAlign: "center",
                    padding: "6px 8px",
                  },
                },
              }}
            />
            <IconButton
              size="small"
              onClick={() => handleQuantityChange(1)}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={18} />
              ) : (
                <Add fontSize="small" />
              )}
            </IconButton>
          </>
        ) : (
          <Button
            variant="outlined"
            size="small"
            onClick={handleAddToCart}
            disabled={loading}
          >
            {loading ? <CircularProgress size={18} /> : "Add to Cart"}
          </Button>
        )}
      </Box>
    </Card>
  );
};

export default ProductCard;
